import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";

interface Credential {
  id: number;
  name: string;
  company_type: string;
  api_url: string;
  username: string;
  address_number: string;
  payment_type: number;
  active: number;
  position: number;
}

export default function ShippingCredentials() {
  const queryClient = useQueryClient();

  const [newName, setNewName] = useState("");
  const [newApiUrl, setNewApiUrl] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newAddressNumber, setNewAddressNumber] = useState("");
  const [newPaymentType, setNewPaymentType] = useState("9");
  const [showAddForm, setShowAddForm] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editApiUrl, setEditApiUrl] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editAddressNumber, setEditAddressNumber] = useState("");
  const [editPaymentType, setEditPaymentType] = useState("9");
  const [editActive, setEditActive] = useState(true);
  const [editPosition, setEditPosition] = useState("0");

  const { data: credentials = [], isLoading } = useQuery<Credential[]>({
    queryKey: ["admin-shipping-credentials"],
    queryFn: () => apiFetch<Credential[]>("/api/admin/shipping-credentials"),
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch("/api/admin/shipping-credentials", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-shipping-credentials"] });
      setNewName("");
      setNewApiUrl("");
      setNewUsername("");
      setNewPassword("");
      setNewAddressNumber("");
      setNewPaymentType("9");
      setShowAddForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Record<string, unknown>) =>
      apiFetch(`/api/admin/shipping-credentials/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-shipping-credentials"] });
      setEditId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/shipping-credentials/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-shipping-credentials"] });
    },
  });

  function handleAdd() {
    if (!newName.trim() || !newApiUrl.trim() || !newUsername.trim() || !newPassword.trim() || !newAddressNumber.trim()) return;
    createMutation.mutate({
      name: newName.trim(),
      company_type: "XE",
      api_url: newApiUrl.trim(),
      username: newUsername.trim(),
      password: newPassword.trim(),
      address_number: newAddressNumber.trim(),
      payment_type: parseInt(newPaymentType) || 9,
      active: true,
      position: 0,
    });
  }

  function startEdit(c: Credential) {
    setEditId(c.id);
    setEditName(c.name);
    setEditApiUrl(c.api_url);
    setEditUsername(c.username);
    setEditPassword("");
    setEditAddressNumber(c.address_number);
    setEditPaymentType(String(c.payment_type));
    setEditActive(c.active === 1);
    setEditPosition(String(c.position));
  }

  function handleSave() {
    if (editId === null || !editName.trim() || !editApiUrl.trim() || !editUsername.trim() || !editAddressNumber.trim()) return;
    updateMutation.mutate({
      id: editId,
      name: editName.trim(),
      company_type: "XE",
      api_url: editApiUrl.trim(),
      username: editUsername.trim(),
      password: editPassword || undefined,
      address_number: editAddressNumber.trim(),
      payment_type: parseInt(editPaymentType) || 9,
      active: editActive,
      position: parseInt(editPosition) || 0,
    });
  }

  function handleDelete(id: number) {
    if (!confirm("Obrisati ovu kurirsku službu?")) return;
    deleteMutation.mutate(id);
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold">Kurirske službe</h1>
        {!showAddForm && (
          <Button size="sm" onClick={() => setShowAddForm(true)}>
            <Plus />
            Dodaj
          </Button>
        )}
      </div>

      {showAddForm && (
        <div className="border rounded-lg p-4 mb-6 space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Nova kurirska služba</h3>
          <Input placeholder="Naziv (npr. X Express - Glavna)" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Input placeholder="API URL" value={newApiUrl} onChange={(e) => setNewApiUrl(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Korisničko ime" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} />
            <Input type="password" placeholder="Lozinka" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Broj lokacije" value={newAddressNumber} onChange={(e) => setNewAddressNumber(e.target.value)} />
            <Input type="number" placeholder="Tip plaćanja (9=pouzeće)" value={newPaymentType} onChange={(e) => setNewPaymentType(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Spremanje..." : "Spremi"}
            </Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>Otkaži</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Učitavanje...</p>
      ) : credentials.length === 0 ? (
        <p className="text-muted-foreground">Nema konfiguriranih kurirskih službi.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {credentials.map((cred) =>
            editId === cred.id ? (
              <div key={cred.id} className="border rounded-lg p-4 space-y-3">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Naziv" />
                <Input value={editApiUrl} onChange={(e) => setEditApiUrl(e.target.value)} placeholder="API URL" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={editUsername} onChange={(e) => setEditUsername(e.target.value)} placeholder="Korisničko ime" />
                  <Input type="password" value={editPassword} onChange={(e) => setEditPassword(e.target.value)} placeholder="Nova lozinka (ostavi prazno)" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input value={editAddressNumber} onChange={(e) => setEditAddressNumber(e.target.value)} placeholder="Br. lokacije" />
                  <Input type="number" value={editPaymentType} onChange={(e) => setEditPaymentType(e.target.value)} placeholder="Tip plaćanja" />
                  <Input type="number" value={editPosition} onChange={(e) => setEditPosition(e.target.value)} placeholder="Pozicija" />
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={editActive}
                      onCheckedChange={(c) => setEditActive(c === true)}
                      className="size-3.5"
                    />
                    Aktivno
                  </label>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                    <Check className="size-4" />
                    Spremi
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditId(null)}>
                    <X className="size-4" />
                    Otkaži
                  </Button>
                </div>
              </div>
            ) : (
              <div key={cred.id} className="flex items-center gap-2 border rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{cred.name}</span>
                  <p className="text-xs text-muted-foreground truncate">{cred.api_url} · Lokacija: {cred.address_number}</p>
                </div>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cred.active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  {cred.active ? "Aktivno" : "Neaktivno"}
                </span>
                <Button size="icon-sm" variant="ghost" onClick={() => startEdit(cred)}>
                  <Pencil />
                </Button>
                <Button size="icon-sm" variant="ghost" onClick={() => handleDelete(cred.id)} disabled={deleteMutation.isPending}>
                  <Trash2 />
                </Button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
